import { isThumbnailableContentType } from '@atlas/types'
import {
	File,
	FileArchive,
	FileAudio,
	FileCode,
	FileImage,
	FileSpreadsheet,
	FileText,
	FileType,
	FileVideo,
	type LucideIcon,
	Presentation,
} from 'lucide-react'

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const

/**
 * One glyph per {@link fileTypeToken}. `File` is the fallback, so a token added to
 * the token map without a matching entry here degrades to a generic icon rather
 * than rendering nothing.
 */
const FILE_TYPE_ICONS: Record<string, LucideIcon> = {
	PDF: FileText,
	SHEET: FileSpreadsheet,
	SLIDE: Presentation,
	DOC: FileText,
	IMG: FileImage,
	PSD: FileImage,
	AI: FileImage,
	VIDEO: FileVideo,
	AUDIO: FileAudio,
	ZIP: FileArchive,
	FONT: FileType,
	CSV: FileSpreadsheet,
	TEXT: FileText,
	SVG: FileCode,
	FILE: File,
}

/** Content types that map to a token directly, ahead of any prefix rule. */
const EXACT_TYPE_TOKENS: Record<string, string> = {
	'image/svg+xml': 'SVG',
	// Ahead of the `image/*` fallback on purpose: a PSD is not a viewable image
	// to the browser, and marking it `IMG` promises a preview that a design file
	// only sometimes has (the worker reads its embedded composite, when present).
	'image/vnd.adobe.photoshop': 'PSD',
	// An .ai shares the `%PDF` header, so it must be labelled before anything keys
	// off the container: it is Illustrator artwork, not a document.
	'application/postscript': 'AI',
	'application/illustrator': 'AI',
	'application/pdf': 'PDF',
	'application/msword': 'DOC',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOC',
	'application/vnd.ms-excel': 'SHEET',
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'SHEET',
	'application/vnd.ms-powerpoint': 'SLIDE',
	'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'SLIDE',
	'text/csv': 'CSV',
	'application/zip': 'ZIP',
	'application/x-zip-compressed': 'ZIP',
}

/**
 * Extensions consulted only inside the `application/octet-stream` branch — a
 * generic binary stream carries no type information of its own, so the name is
 * the only signal left.
 */
const OCTET_STREAM_EXTENSION_TOKENS: Record<string, string> = {
	svg: 'SVG',
	png: 'IMG',
	jpg: 'IMG',
	jpeg: 'IMG',
	gif: 'IMG',
	webp: 'IMG',
	tif: 'IMG',
	tiff: 'IMG',
	bmp: 'IMG',
	heic: 'IMG',
	avif: 'IMG',
	ai: 'AI',
	psd: 'PSD',
	mp4: 'VIDEO',
	mov: 'VIDEO',
	avi: 'VIDEO',
	webm: 'VIDEO',
	mpeg: 'VIDEO',
	mpg: 'VIDEO',
	mp3: 'AUDIO',
	wav: 'AUDIO',
	ogg: 'AUDIO',
	pdf: 'PDF',
	doc: 'DOC',
	docx: 'DOC',
	xls: 'SHEET',
	xlsx: 'SHEET',
	ppt: 'SLIDE',
	pptx: 'SLIDE',
	csv: 'CSV',
	txt: 'TEXT',
	zip: 'ZIP',
	otf: 'FONT',
	ttf: 'FONT',
	woff: 'FONT',
	woff2: 'FONT',
}

/** Font extensions, used only as a last resort — fonts have no single canonical MIME. */
const FONT_EXTENSIONS = new Set(['otf', 'ttf', 'woff', 'woff2'])

function fileExtension(fileName: string | undefined): string {
	if (!fileName) return ''
	const lastDot = fileName.lastIndexOf('.')
	return lastDot === -1 ? '' : fileName.slice(lastDot + 1).toLowerCase()
}

/**
 * A short, recognisable marker for a File's binary type, shown in a card's
 * preview area when no thumbnail is available.
 *
 * Describes what the File IS and says nothing about whether a thumbnail can be
 * generated for it, so the marker and the processing pipeline can never drift
 * into a visible inconsistency. A specific valid content type always wins: the
 * filename is consulted only for a generic binary stream, or as a final font
 * fallback. Unknown, missing, or malformed input degrades to `FILE`; the result
 * is never empty.
 */
export function fileTypeToken(contentType: string | null | undefined, fileName?: string): string {
	const normalized = contentType?.trim().toLowerCase() ?? ''
	if (normalized === '' || !normalized.includes('/')) return 'FILE'

	const extension = fileExtension(fileName)
	if (normalized === 'application/octet-stream') return OCTET_STREAM_EXTENSION_TOKENS[extension] ?? 'FILE'

	const exact = EXACT_TYPE_TOKENS[normalized]
	if (exact) return exact

	if (normalized.startsWith('image/')) return 'IMG'
	if (normalized.startsWith('video/')) return 'VIDEO'
	if (normalized.startsWith('audio/')) return 'AUDIO'
	if (normalized.startsWith('text/')) return 'TEXT'
	if (normalized.startsWith('font/') || normalized.startsWith('application/font-')) return 'FONT'

	return FONT_EXTENSIONS.has(extension) ? 'FONT' : 'FILE'
}

/**
 * The lucide glyph that goes with a {@link fileTypeToken}, for a tile whose file
 * has no thumbnail yet (or will never have one).
 *
 * Returns the component *reference* rather than rendered JSX so this module stays
 * a plain `.ts` with no React dependency — callers wrap it in the `Icon` primitive,
 * which is what applies the design-token sizing.
 */
export function fileTypeIcon(token: string): LucideIcon {
	return FILE_TYPE_ICONS[token] ?? File
}

/**
 * A short marker naming a file's binary type, for a tile or card with no thumbnail —
 * `SVG`, `IMG`, `VIDEO`, `PDF`, `FONT`, else the subtype clipped to four letters.
 *
 * Shared by the Library and Explore surfaces so those views cannot label the same
 * content type differently.
 */
export function typeMarker(contentType: string): string {
	if (contentType === 'image/svg+xml') return 'SVG'
	if (contentType === 'image/vnd.adobe.photoshop') return 'PSD'
	// Before the generic subtype fallback, which would clip this to `POST`.
	if (contentType === 'application/postscript') return 'AI'
	if (contentType.startsWith('image/')) return 'IMG'
	if (contentType.startsWith('video/')) return 'VIDEO'
	if (contentType === 'application/pdf') return 'PDF'
	if (contentType.includes('font')) return 'FONT'
	return contentType.split('/')[1]?.slice(0, 4).toUpperCase() ?? 'FILE'
}

/**
 * Whether a file can carry a server-generated thumbnail; everything else gets a
 * {@link typeMarker}.
 *
 * Delegates to the one list the worker's concern registry is built from, so this
 * cannot fall behind what the backend actually renders. It asks "could a thumbnail
 * exist for this type", not "does this one have one" — callers still fall back to
 * the marker when `thumbnailUrl` is absent, which is what covers a render that
 * failed (an encrypted PDF, or an .ai saved without PDF compatibility).
 */
export const isPreviewable = (contentType: string): boolean => isThumbnailableContentType(contentType)

/** Human-readable byte size, e.g. `1.5 MB`. Returns `-` when unknown. */
export function formatBytes(bytes: number | null): string {
	if (bytes === null || Number.isNaN(bytes) || bytes < 0) return '-'
	if (bytes === 0) return '0 B'
	const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), BYTE_UNITS.length - 1)
	const value = bytes / 1024 ** exponent
	const rounded = value >= 100 || Number.isInteger(value) ? Math.round(value).toString() : value.toFixed(1)
	return `${rounded} ${BYTE_UNITS[exponent]}`
}
